/** @format */
import { ChangeDetectionStrategy, Component, Input, OnDestroy, computed } from '@angular/core';
import {
	CompareBarProduct,
	CompareProductDetails,
	ProductListSEO
} from '@shared/data-access/interfaces';
import { Subject, takeUntil } from 'rxjs';
import {
	selectCompareProductDetails,
	selectFeatureFlags,
	selectProductListSEO
} from '@plp/data-access/state';

import { AnalyticsService } from '@shared/utils/services';
import { CommonModule } from '@angular/common';
import { CompareThumbnailComponent } from './compare-thumbnail/compare-thumbnail.component';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { ProductListCompareService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';
import { selectUserStoreNumber } from '@pdp/data-access/state';

@Component({
	selector: 'plp-components-compare-bar',
	imports: [CommonModule, CompareThumbnailComponent, HmfButtonComponent],
	templateUrl: './compare-bar.component.html',
	styleUrl: './compare-bar.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareBarComponent implements OnDestroy {
	private readonly UNSUBSCRIBE$ = new Subject<void>();

	readonly selectedStoreId$ = this.store
		.select(selectUserStoreNumber)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productListSeo$ = this.store
		.select(selectProductListSEO)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly compareProductDetails$ = this.store
		.select(selectCompareProductDetails)
		.pipe(takeUntil(this.UNSUBSCRIBE$));

	@Input() compareList: CompareBarProduct[] = [];

	selectedStore = '';
	productListSeoData: ProductListSEO | null = null;
	productDetails: CompareProductDetails | null = null;

	readonly featureFlags = this.store.selectSignal(selectFeatureFlags);
	compareGridEnabled = computed(() => this.featureFlags().compareGridEnabled);

	constructor(
		private readonly plpCompareService: ProductListCompareService,
		private readonly analyticsService: AnalyticsService,
		private readonly store: Store
	) {
		this.selectedStoreId$.subscribe((selectedStoreId) => {
			this.selectedStore = selectedStoreId;
		});

		this.productListSeo$.subscribe((seo) => {
			this.productListSeoData = seo;
		});

		this.compareProductDetails$.subscribe((details) => {
			this.productDetails = details;
		});
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	removeFromCompareList(ecode: string): void {
		this.plpCompareService.removeProductFromCompareList(ecode);
	}

	clearAllCompareList(): void {
		this.plpCompareService.clearCompareList();
	}

	compareProducts(): void {
		if (this.compareGridEnabled()) {
			this.plpCompareService.openCompareGrid();
			this.verifyAndCallProductDetailsFromApi();
		} else {
			this.plpCompareService.openPaintedDoorModal();
		}

		this.compareProductsAnalytics();
	}

	private compareProductsAnalytics(): void {
		const compareAnalyticsData = {
			ModalName: 'Compare Products',
			ProductArray: []
		};
		this.compareList.forEach((product) => {
			compareAnalyticsData.ProductArray.push({
				ProductID: product.ecode,
				CompareSource: product.compareSource
			});
		});
		this.analyticsService.triggerAnalyticsEvent('CompareOpened', compareAnalyticsData);
	}

	private verifyAndCallProductDetailsFromApi(): void {
		const ecodes = this.compareList.map((product) => product.ecode);
		const storeEcodes = this.productDetails?.products?.map(
			(product) => product.style.partNumber
		);

		const newEcodes = ecodes.filter((ecode: string) => !storeEcodes?.includes(ecode));

		if (newEcodes.length === 0) {
			return;
		}

		const isClearance = this.productListSeoData?.identifier?.startsWith('Clearance');
		this.plpCompareService.getProductDetailsFromApi(ecodes, isClearance);
	}

	protected trackByFn(index: number, item: CompareBarProduct): string {
		return `${index.toString()}-${item.ecode}`;
	}
}
